import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { secondsToRuntime } from 'src/client/utils/converters';
import { faCameraAlt } from '@fortawesome/pro-light-svg-icons';
import { faVideo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from '../../Spinner/Spinner';
import { BlurrableImage } from '../../ui/BlurredImage';

type Props = {
  blurImage: boolean;
  galleryCoverUrl: string;
  galleryUrl: string;
  imageCount?: number;
  models?: string;
  publishedAt?: string;
  runtime?: number;
  spriteUrl: string | null;
  title: string;
  type: string;
};

const GalleryWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const GalleryOverlay = styled.div`
  z-index: 0;
  color: transparent;
  position: absolute;
  flex-basis: 100%;
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;

  img {
    transition: 0.3s;
    opacity: 0;
  }
  :hover {
    color: #fff;
    background: rgba(0, 0, 0, 0.7);
    img {
      opacity: 1;
    }
  }
  @media (max-width: 1024px) {
    font-size: 48px;
    img {
      max-width: 44px;
    }
  }
`;

const GalleryInformation = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 0 0 20px 20px;
  width: 100%;
  max-width: 97%;
  z-index: 2;
  @media (max-width: 1024px) {
    padding: 0 0 12px 9px;
  }
`;

const GalleryTitle = styled.span`
  display: block;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 800;
  font-size: 28px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1024px) {
    font-size: 16px;
  }
`;

const GalleryModels = styled.span`
  display: block;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 1024px) {
    font-size: 11px;
  }
  @media (max-width: 414px) {
    font-size: 10px;
  }
`;

const GalleryDate = styled.span`
  display: block;
  font-size: 14px;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 400;
  color: #9d9d9d;
  @media (max-width: 1024px) {
    font-size: 8px;
  }
`;

const GalleryCount = styled.span`
  display: block;
  width: 80px;
  height: 38px;
  line-height: 38px;
  border-radius: 10px;
  background: #151419;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  position: absolute;
  right: 0;
  bottom: 10px;
  @media (max-width: 1024px) {
    width: 60px;
    height: 21px;
    line-height: 21px;
    font-size: 11px;
    bottom: 5px;
  }
`;

const GalleryLink = styled(Link)`
  color: #fff;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-size: 24px;
  position: relative;
  text-decoration: none;
  overflow: hidden;
  position: relative;

  @media (max-width: 1024px) {
    font-size: 14px;
  }
  :hover,
  :focus {
    text-decoration: none;
    color: #fff;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background: linear-gradient(0, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%);

    @media (max-width: 1024px) {
      background: linear-gradient(0, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 50%);
    }
  }
`;

const GalleryImage = styled(BlurrableImage)`
  z-index: 0;
  max-width: 100%;
  @media (max-width: 1024px) {
    vertical-align: baseline;
  }
`;

const HoverPreviewWrapper = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 214/120;
  position: absolute;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  @media (max-width: 992px) {
    pointer-events: none;
  }

  img {
    display: none;
  }
`;

const HoverPreview = styled.span<{ $spriteUrl: string }>`
  display: block;
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
  animation: play 12s steps(45) infinite;
  box-shadow: 0 -6px 12px 3px #00000060;
  background-image: url(${(props) => props.$spriteUrl});
  background-position-x: center;
  background-repeat: no-repeat;

  --height: 19vw;
  --width: 100%;
  // width + 1 because there is a 1px gap between the frames, so that needs to be off the screen.
  --background-width: calc(var(--width) + 0.5vw);
  // height * 48 because there are 48 frames in the sprite
  --background-height: calc(var(--height) * 48);
  // skip the first 3 frames
  --background-start: calc(var(--height) * -3);
  --background-end: calc(var(--background-height) * -1);

  width: var(--width);
  height: var(--height);
  background-size: var(--background-width) var(--background-height);
  background-position-y: var(--background-start);

  @keyframes play {
    from {
      background-position-y: var(--background-start);
    }
    to {
      background-position-y: var(--background-end);
    }
  }

  @media (max-width: 430px) {
    --height: 55vw;
  }
`;

export const Gallery = (props: Props) => {
  const cdnUrl = useSelector((state) => state.app.cdnUrl);
  const site = useSelector((state) => state.site);

  const [showSprite, setShowSprite] = useState(false);
  const [isSpriteLoaded, setSpriteLoaded] = useState(false);
  const { spriteUrl } = props;

  const showPreview = () => {
    if (!spriteUrl) return;
    setShowSprite(true);
    setSpriteLoaded(false);
  };

  const hidePreview = () => {
    if (!spriteUrl) return;
    setShowSprite(false);
  };

  const showHoverPreview = spriteUrl && showSprite;

  return (
    <GalleryWrapper
      className={props.type}
      onMouseEnter={showPreview}
      onTouchStart={showPreview}
      onTouchEnd={hidePreview}
      onMouseLeave={hidePreview}
    >
      <GalleryLink to={props.galleryUrl}>
        {showHoverPreview && (
          <HoverPreviewWrapper>
            <img src={spriteUrl || ''} alt="preview" onLoad={() => setSpriteLoaded(true)} />
            {!isSpriteLoaded ? <Spinner size="20%" /> : <HoverPreview $spriteUrl={spriteUrl || ''} />}
          </HoverPreviewWrapper>
        )}

        <GalleryOverlay>
          {(props.type == 'GALLERY' && <img src={`${cdnUrl}/splash/${site.networkUUID}/view.png`} alt="View" />) || (
            <img src={`${cdnUrl}/splash/${site.networkUUID}/play.png`} alt="Play" />
          )}
        </GalleryOverlay>

        <GalleryImage src={props.galleryCoverUrl} alt={props.title} $isBlurred={props.blurImage} />

        <GalleryInformation>
          <GalleryTitle>{props.title}</GalleryTitle>
          <GalleryModels>{props.models}</GalleryModels>
          <GalleryDate>{props.publishedAt}</GalleryDate>
          {props.type == 'GALLERY' && (
            <GalleryCount>
              <FontAwesomeIcon icon={faCameraAlt} /> {props.imageCount}
            </GalleryCount>
          )}
          {props.type == 'MOVIE' && (
            <GalleryCount>
              <FontAwesomeIcon icon={faVideo} /> {secondsToRuntime(props.runtime)}
            </GalleryCount>
          )}
        </GalleryInformation>
      </GalleryLink>
    </GalleryWrapper>
  );
};
